import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Spinner, Badge, Tabs, Tab } from 'react-bootstrap';
import { useTranslation } from '../../../../i18n/I18nProvider';
import ImagePicker from '../../../../components/ImagePicker/ImagePicker';

const initialFormData = {
  title: '',
  titles: { fr: '', en: '' },
  description: '',
  descriptions: { fr: '', en: '' },
  tags: '',
  cover: '',
  isActive: true,
  subServices: [],
  slug: '',
  subtitle: '',
  subtitles: { fr: '', en: '' },
  featuredKeys: [],
};

function ServiceForm({ initialData, onSubmit, onCancel, loading, isEdit = false }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [imagePickerTarget, setImagePickerTarget] = useState(null);

  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title || '',
        titles: {
          fr: initialData.titles?.fr || initialData.title || '',
          en: initialData.titles?.en || '',
        },
        description: initialData.description || '',
        descriptions: {
          fr: initialData.descriptions?.fr || initialData.description || '',
          en: initialData.descriptions?.en || '',
        },
        tags: Array.isArray(initialData.tags) ? initialData.tags.join(', ') : '',
        cover: initialData.cover || '',
        isActive: initialData.isActive !== false,
        subServices: Array.isArray(initialData.subServices)
          ? initialData.subServices.map(s => ({
              key: s.key || '',
              labels: {
                en: s.labels?.en || s.label || '',
                fr: s.labels?.fr || s.label || '',
              },
              cover: s.cover || '',
              description: {
                en: s.description?.en || '',
                fr: s.description?.fr || '',
              },
              items: Array.isArray(s.items)
                ? s.items.map(it => ({ en: it.en || '', fr: it.fr || '' }))
                : [],
            }))
          : [],
        slug: initialData.slug || '',
        subtitle: initialData.subtitle || '',
        subtitles: {
          fr: initialData.subtitles?.fr || initialData.subtitle || '',
          en: initialData.subtitles?.en || '',
        },
        featuredKeys: Array.isArray(initialData.featuredKeys) ? [...initialData.featuredKeys] : [],
      });
    } else {
      setFormData(initialFormData);
    }
  }, [initialData]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = t('admin.services.validation.titleRequired');
    }

    if (formData.title.length > 100) {
      newErrors.title = t('admin.services.validation.titleTooLong');
    }

    if (formData.description && formData.description.length > 1000) {
      newErrors.description = t('admin.services.validation.descriptionTooLong');
    }

    if (formData.cover && !isValidUrl(formData.cover)) {
      newErrors.cover = t('admin.services.validation.invalidUrl');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const submitData = {
      title: formData.title.trim(),
      titles: {
        fr: formData.titles.fr.trim() || undefined,
        en: formData.titles.en.trim() || undefined,
      },
      description: formData.description.trim(),
      descriptions: {
        fr: formData.descriptions.fr.trim() || undefined,
        en: formData.descriptions.en.trim() || undefined,
      },
      tags: formData.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0),
      cover: formData.cover.trim() || undefined,
      isActive: formData.isActive,
      slug: formData.slug.trim() || undefined,
      subtitle: formData.subtitle.trim() || undefined,
      subtitles: {
        fr: formData.subtitles.fr.trim() || undefined,
        en: formData.subtitles.en.trim() || undefined,
      },
      subServices: formData.subServices
        .filter(s => (s.labels?.fr?.trim() || s.labels?.en?.trim()))
        .map(s => ({
          key: s.key.trim() || slugify(s.labels?.fr || s.labels?.en || ''),
          label: s.labels?.fr?.trim() || s.labels?.en?.trim() || undefined,
          labels: {
            en: s.labels?.en?.trim() || undefined,
            fr: s.labels?.fr?.trim() || undefined,
          },
          cover: s.cover.trim() || undefined,
          description: {
            en: s.description?.en?.trim() || undefined,
            fr: s.description?.fr?.trim() || undefined,
          },
          items: (s.items || [])
            .filter(it => it.en?.trim() || it.fr?.trim())
            .map(it => ({
              en: it.en?.trim() || undefined,
              fr: it.fr?.trim() || undefined,
            })),
        })),
      featuredKeys: formData.featuredKeys,
    };

    onSubmit(submitData);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // ===== Sub-service management =====
  const slugify = (str) => {
    return str
      .toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  };

  const addSubService = () => {
    setFormData(prev => ({
      ...prev,
      subServices: [...prev.subServices, { key: '', labels: { en: '', fr: '' }, cover: '', description: { en: '', fr: '' }, items: [] }],
    }));
  };

  const removeSubService = (idx) => {
    setFormData(prev => ({
      ...prev,
      subServices: prev.subServices.filter((_, i) => i !== idx),
      featuredKeys: prev.featuredKeys.filter(k => k !== prev.subServices[idx]?.key),
    }));
  };

  const updateSubService = (idx, field, value) => {
    setFormData(prev => ({
      ...prev,
      subServices: prev.subServices.map((s, i) => {
        if (i !== idx) return s;
        const updated = { ...s, [field]: value };
        if (field === 'labels') {
          updated.key = slugify(value.fr || value.en || '');
        }
        return updated;
      }),
    }));
  };

  const addSubItem = (subIdx) => {
    setFormData(prev => ({
      ...prev,
      subServices: prev.subServices.map((s, i) =>
        i === subIdx ? { ...s, items: [...(s.items || []), { en: '', fr: '' }] } : s
      ),
    }));
  };

  const removeSubItem = (subIdx, itemIdx) => {
    setFormData(prev => ({
      ...prev,
      subServices: prev.subServices.map((s, i) =>
        i === subIdx ? { ...s, items: (s.items || []).filter((_, j) => j !== itemIdx) } : s
      ),
    }));
  };

  const updateSubItem = (subIdx, itemIdx, lang, value) => {
    setFormData(prev => ({
      ...prev,
      subServices: prev.subServices.map((s, i) =>
        i === subIdx
          ? { ...s, items: (s.items || []).map((it, j) => j === itemIdx ? { ...it, [lang]: value } : it) }
          : s
      ),
    }));
  };

  const toggleFeaturedKey = (key) => {
    if (!key) return;
    setFormData(prev => ({
      ...prev,
      featuredKeys: prev.featuredKeys.includes(key)
        ? prev.featuredKeys.filter(k => k !== key)
        : [...prev.featuredKeys, key],
    }));
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Tabs defaultActiveKey="general" className="mb-3">

        {/* ===== Tab: General ===== */}
        <Tab eventKey="general" title={t('admin.services.form.tabGeneral')}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{t('admin.services.form.title')} *</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  isInvalid={!!errors.title}
                  placeholder={t('admin.services.form.titlePlaceholder')}
                  disabled={loading}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.title}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  {t('admin.services.form.titleHelp')}
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{t('admin.services.form.cover')}</Form.Label>
                <div className="d-flex gap-2 align-items-start">
                  <div className="flex-grow-1">
                    <Form.Control
                      type="text"
                      value={formData.cover}
                      onChange={(e) => handleInputChange('cover', e.target.value)}
                      isInvalid={!!errors.cover}
                      placeholder={t('admin.services.form.coverPlaceholder')}
                      disabled={loading}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.cover}
                    </Form.Control.Feedback>
                  </div>
                  <Button
                    type="button"
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => { setImagePickerTarget('cover'); setShowImagePicker(true); }}
                    disabled={loading}
                  >
                    {t('admin.services.form.browse')}
                  </Button>
                </div>
                {formData.cover && (
                  <div className="mt-2 border rounded p-1 d-inline-block">
                    <img
                      src={formData.cover}
                      alt="Cover preview"
                      style={{ maxWidth: 200, maxHeight: 120, objectFit: 'cover', borderRadius: 4 }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}
                <Form.Text className="text-muted d-block">
                  {t('admin.services.form.coverHelp')}
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="small fw-semibold">FR</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.titles.fr}
                  onChange={(e) => setFormData(prev => ({ ...prev, titles: { ...prev.titles, fr: e.target.value } }))}
                  placeholder="Titre (FR)"
                  disabled={loading}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="small fw-semibold">EN</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.titles.en}
                  onChange={(e) => setFormData(prev => ({ ...prev, titles: { ...prev.titles, en: e.target.value } }))}
                  placeholder="Title (EN)"
                  disabled={loading}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>{t('admin.services.form.description')}</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              isInvalid={!!errors.description}
              placeholder={t('admin.services.form.descriptionPlaceholder')}
              disabled={loading}
            />
            <Form.Control.Feedback type="invalid">
              {errors.description}
            </Form.Control.Feedback>
          </Form.Group>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="small fw-semibold">{t('admin.services.form.description')} FR</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={formData.descriptions.fr}
                  onChange={(e) => setFormData(prev => ({ ...prev, descriptions: { ...prev.descriptions, fr: e.target.value } }))}
                  placeholder="Description (FR)"
                  disabled={loading}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="small fw-semibold">{t('admin.services.form.description')} EN</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={formData.descriptions.en}
                  onChange={(e) => setFormData(prev => ({ ...prev, descriptions: { ...prev.descriptions, en: e.target.value } }))}
                  placeholder="Description (EN)"
                  disabled={loading}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{t('admin.services.form.tags')}</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.tags}
                  onChange={(e) => handleInputChange('tags', e.target.value)}
                  placeholder={t('admin.services.form.tagsPlaceholder')}
                  disabled={loading}
                />
                <Form.Text className="text-muted">
                  {t('admin.services.form.tagsHelp')}
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{t('admin.services.form.subtitle')}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={formData.subtitle}
                  onChange={(e) => handleInputChange('subtitle', e.target.value)}
                  placeholder={t('admin.services.form.subtitlePlaceholder')}
                  disabled={loading}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="small fw-semibold">{t('admin.services.form.subtitle')} FR</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={formData.subtitles.fr}
                  onChange={(e) => setFormData(prev => ({ ...prev, subtitles: { ...prev.subtitles, fr: e.target.value } }))}
                  placeholder="Sous-titre (FR)"
                  disabled={loading}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="small fw-semibold">{t('admin.services.form.subtitle')} EN</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={formData.subtitles.en}
                  onChange={(e) => setFormData(prev => ({ ...prev, subtitles: { ...prev.subtitles, en: e.target.value } }))}
                  placeholder="Subtitle (EN)"
                  disabled={loading}
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              id="service-active"
              label={t('admin.services.form.isActive')}
              checked={formData.isActive}
              onChange={(e) => handleInputChange('isActive', e.target.checked)}
              disabled={loading}
            />
            <Form.Text className="text-muted">
              {t('admin.services.form.isActiveHelp')}
            </Form.Text>
          </Form.Group>
        </Tab>

        {/* ===== Tab: Sub-services ===== */}
        <Tab
          eventKey="subServices"
          title={
            <span>
              {t('admin.services.form.tabSubServices')}
              {formData.subServices.length > 0 && (
                <Badge bg="primary" pill className="ms-1" style={{ fontSize: '0.65rem' }}>
                  {formData.subServices.length}
                </Badge>
              )}
            </span>
          }
        >
          <Form.Group className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <Form.Label className="mb-0">{t('admin.services.form.subServices')}</Form.Label>
              <Button
                type="button"
                variant="outline-primary"
                size="sm"
                onClick={addSubService}
                disabled={loading}
              >
                + {t('admin.services.form.addSubService')}
              </Button>
            </div>
            <Form.Text className="text-muted d-block mb-3">
              {t('admin.services.form.subServicesHelp')}
            </Form.Text>
            {formData.subServices.length > 0 ? (
              <div className="d-flex flex-column gap-3">
                {formData.subServices.map((sub, idx) => (
                  <div key={idx} className="border rounded p-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="small text-muted fw-medium">
                        {sub.labels?.fr || sub.labels?.en || t('admin.services.form.untitled')}
                      </span>
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="p-0 text-decoration-none text-danger"
                        onClick={() => removeSubService(idx)}
                        disabled={loading}
                      >
                        ✕
                      </Button>
                    </div>
                    <Row className="g-2">
                      <Col xs={12} md={6}>
                        <Form.Group>
                          <Form.Label className="small mb-1">{t('admin.services.form.labelFr')}</Form.Label>
                          <Form.Control
                            type="text"
                            size="sm"
                            value={sub.labels?.fr || ''}
                            onChange={(e) => updateSubService(idx, 'labels', { ...sub.labels, fr: e.target.value })}
                            placeholder={t('admin.services.form.labelFrPlaceholder')}
                            disabled={loading}
                          />
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Group>
                          <Form.Label className="small mb-1">{t('admin.services.form.labelEn')}</Form.Label>
                          <Form.Control
                            type="text"
                            size="sm"
                            value={sub.labels?.en || ''}
                            onChange={(e) => updateSubService(idx, 'labels', { ...sub.labels, en: e.target.value })}
                            placeholder={t('admin.services.form.labelEnPlaceholder')}
                            disabled={loading}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Form.Group className="mt-2">
                      <Form.Label className="small mb-1">{t('admin.services.form.subServiceCover')}</Form.Label>
                      <div className="d-flex align-items-center gap-2">
                        <Form.Control
                          type="text"
                          size="sm"
                          value={sub.cover}
                          onChange={(e) => updateSubService(idx, 'cover', e.target.value)}
                          placeholder={t('admin.services.form.subServiceCover')}
                          disabled={loading}
                          style={{ maxWidth: 250 }}
                        />
                        <Button
                          type="button"
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => { setImagePickerTarget({ type: 'subService', idx }); setShowImagePicker(true); }}
                          disabled={loading}
                        >
                          ...
                        </Button>
                        {sub.cover && (
                          <img
                            src={sub.cover}
                            alt=""
                            style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6, border: '1px solid #dee2e6' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )}
                      </div>
                    </Form.Group>
                    <div className="mt-2">
                      <Form.Label className="small text-muted mb-1">{t('admin.services.form.description')}</Form.Label>
                      <Row className="g-2">
                        <Col xs={12} md={6}>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            size="sm"
                            value={sub.description?.fr || ''}
                            onChange={(e) => updateSubService(idx, 'description', { ...sub.description, fr: e.target.value })}
                            placeholder={t('admin.services.form.descriptionFrPlaceholder')}
                            disabled={loading}
                          />
                        </Col>
                        <Col xs={12} md={6}>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            size="sm"
                            value={sub.description?.en || ''}
                            onChange={(e) => updateSubService(idx, 'description', { ...sub.description, en: e.target.value })}
                            placeholder={t('admin.services.form.descriptionEnPlaceholder')}
                            disabled={loading}
                          />
                        </Col>
                      </Row>
                    </div>
                    <div className="mt-2">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <Form.Label className="small text-muted mb-0">{t('admin.services.form.itemsList')}</Form.Label>
                        <Button
                          type="button"
                          variant="outline-secondary"
                          size="sm"
                          className="py-0 px-1"
                          style={{ fontSize: '0.7rem' }}
                          onClick={() => addSubItem(idx)}
                          disabled={loading}
                        >
                          + {t('admin.services.form.addItem')}
                        </Button>
                      </div>
                      {(sub.items || []).length > 0 && (
                        <div className="d-flex flex-column gap-1">
                          {sub.items.map((item, iIdx) => (
                            <div key={iIdx} className="d-flex align-items-center gap-1">
                              <Form.Control
                                type="text"
                                size="sm"
                                value={item.fr || ''}
                                onChange={(e) => updateSubItem(idx, iIdx, 'fr', e.target.value)}
                                placeholder={t('admin.services.form.itemFrPlaceholder')}
                                disabled={loading}
                                style={{ fontSize: '0.8rem' }}
                              />
                              <Form.Control
                                type="text"
                                size="sm"
                                value={item.en || ''}
                                onChange={(e) => updateSubItem(idx, iIdx, 'en', e.target.value)}
                                placeholder={t('admin.services.form.itemEnPlaceholder')}
                                disabled={loading}
                                style={{ fontSize: '0.8rem' }}
                              />
                              <Button
                                type="button"
                                variant="link"
                                size="sm"
                                className="p-0 text-decoration-none text-danger flex-shrink-0"
                                onClick={() => removeSubItem(idx, iIdx)}
                                disabled={loading}
                              >
                                ✕
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-muted py-4">
                <p className="mb-2">{t('admin.services.form.noSubServices')}</p>
              </div>
            )}
          </Form.Group>
        </Tab>

        {/* ===== Tab: Routing ===== */}
        <Tab eventKey="routing" title={t('admin.services.form.tabRouting')}>
          <Form.Group className="mb-3">
            <Form.Label>{t('admin.services.form.slug')}</Form.Label>
            <Form.Control
              type="text"
              value={formData.slug}
              onChange={(e) => handleInputChange('slug', e.target.value)}
              placeholder={t('admin.services.form.slugPlaceholder')}
              disabled={loading}
            />
            <Form.Text className="text-muted">
              {t('admin.services.form.slugHelp')}
            </Form.Text>
          </Form.Group>

          {formData.slug && (
            <div className="border rounded p-3 bg-light">
              <small className="text-muted d-block mb-1">{t('admin.services.form.previewUrl')}</small>
              <code>/services/{formData.slug}</code>
            </div>
          )}
        </Tab>

      </Tabs>

      <ImagePicker
        show={showImagePicker}
        onHide={() => setShowImagePicker(false)}
        onSelect={(path) => {
          if (imagePickerTarget === 'cover') {
            handleInputChange('cover', path);
          } else if (imagePickerTarget?.type === 'subService') {
            updateSubService(imagePickerTarget.idx, 'cover', path);
          }
        }}
        currentPath={
          imagePickerTarget === 'cover'
            ? formData.cover
            : imagePickerTarget?.type === 'subService'
              ? formData.subServices[imagePickerTarget.idx]?.cover
              : null
        }
      />

      <div className="d-flex gap-2">
        <Button
          type="submit"
          variant="primary"
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
              <span className="ms-2">
                {isEdit ? t('admin.services.form.updating') : t('admin.services.form.creating')}
              </span>
            </>
          ) : (
            isEdit ? t('admin.services.form.update') : t('admin.services.form.create')
          )}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={loading}
        >
          {t('common.cancel')}
        </Button>
      </div>
    </Form>
  );
}

export default ServiceForm;
